import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import { ApiResponse } from '../shared/types';

/**
 * CWS-1426: the Update Membership flow — what a membership can change to and
 * what each option costs (server-computed), and the change itself.
 */
export type PlanChangeTiming = 'immediate' | 'next_cycle';
export type PlanChangeDirection = 'upgrade' | 'downgrade';
export type PlanChangeRefundOption = 'none' | 'prorated' | 'full' | 'custom';

export interface PlanChangePlanSummary {
  package_id: string;
  name: string;
  monthly_price: number | null;
  /** CWS-1579: the package's list price; differs from `monthly_price` for a family member. */
  list_price?: number | null;
  unlimited: boolean;
  washes_per_cycle: number | null;
  theme_color: string | null;
}

export interface PlanChangeRefundChoice {
  option: PlanChangeRefundOption;
  /** null for `custom`, which carries `max_amount` instead. */
  amount: number | null;
  max_amount?: number;
}

export interface PlanChangeWashAllowance {
  unlimited: boolean;
  washes_this_cycle_default: number | null;
  washes_per_renewal: number | null;
  override_max: number | null;
}

export interface PlanChangeOption extends PlanChangePlanSummary {
  direction: PlanChangeDirection;
  price_difference_per_month: number;
  next_cycle: { charged_today: number; price_from_renewal: number };
  /** null on the renewal day, when only a next-cycle change is allowed. */
  immediate: {
    charged_today: number;
    prorated_difference: number;
    full_difference: number;
    refund_options: PlanChangeRefundChoice[] | null;
    wash_allowance: PlanChangeWashAllowance;
  } | null;
}

export interface PlanChangeMembership {
  subscription_id: string;
  status: string;
  customer_name: string | null;
  customer_contact: string | null;
  license_plate: string | null;
  current_plan: PlanChangePlanSummary;
  /** CWS-1579: set when the plate is in a family plan. */
  family?: {
    role: 'primary' | 'member';
    discount_type: 'percentage' | 'flat' | 'custom' | null;
    discount_value: number | null;
  };
}

export interface PlanChangeCycle {
  start: string;
  end: string;
  renewal_date: string;
  total_days: number;
  remaining_days: number;
  factor: number;
  immediate_allowed: boolean;
  washes_used_this_cycle: number;
  timezone: string;
}

/** The card a refund would go back to, read from the membership's last payment. */
export interface PlanChangeRefundDestination {
  brand: string | null;
  last4: string | null;
}

export interface PlanChangeOptions {
  membership: PlanChangeMembership;
  eligible: boolean;
  /** 'ok' when eligible; otherwise a stable reason such as 'pending_change_exists'. */
  reason: string;
  pending_change?: {
    id: string;
    new_package_id: string;
    timing: PlanChangeTiming;
    effective_at: string;
  };
  cycle?: PlanChangeCycle;
  refund_destination?: PlanChangeRefundDestination | null;
  upgrades?: PlanChangeOption[];
  downgrades?: PlanChangeOption[];
}

export interface PlanChangeRequest {
  /** Client-generated UUID; a double tap with the same id returns the first result. */
  request_id: string;
  location_id: string;
  new_package_id: string;
  timing: PlanChangeTiming;
  refund_option?: PlanChangeRefundOption;
  custom_refund_amount?: number;
  refund_reason?: string;
  wash_allowance_override?: number;
}

export interface PlanChangeResult {
  replayed: boolean;
  status?: string;
  failure_reason?: string | null;
  change: {
    id: string;
    status: string;
    timing: PlanChangeTiming;
    direction?: PlanChangeDirection;
    charged_amount: number | string;
    refunded_amount: number | string;
  };
  membership?: {
    subscription_id: string;
    old_plan: PlanChangePlanSummary;
    new_plan: PlanChangePlanSummary;
    renewal_date: string;
    washes_this_cycle: number | null;
    washes_per_renewal: number | null;
    unlimited: boolean;
  };
  money?: {
    charged_today: number;
    refunded_today: number;
    refund_capped_at: number | null;
    price_from_renewal: number;
  };
}

/** Error body the plan-change endpoints return for a 4xx with a stable `code`. */
export interface PlanChangeErrorBody {
  status: false;
  status_code: number;
  code?: string;
  message?: string;
}

@Injectable({ providedIn: 'root' })
export class PlanChangeService {
  constructor(private api: ApiService) {}

  getOptions(
    subscriptionId: string,
    locationId: string
  ): Observable<ApiResponse<PlanChangeOptions>> {
    return this.api.get<PlanChangeOptions>(
      `order/membership-plan-change/subscriptions/${encodeURIComponent(subscriptionId)}/options`,
      { location_id: locationId }
    );
  }

  createChange(
    subscriptionId: string,
    body: PlanChangeRequest
  ): Observable<ApiResponse<PlanChangeResult>> {
    return this.api.post<PlanChangeResult>(
      `order/membership-plan-change/subscriptions/${encodeURIComponent(subscriptionId)}/changes`,
      body
    );
  }

  /** Cancel a scheduled (next-cycle) change; refused once the renewal invoice exists. */
  cancelChange(
    subscriptionId: string,
    changeId: string
  ): Observable<ApiResponse<{ change: PlanChangeResult['change'] }>> {
    return this.api.post<{ change: PlanChangeResult['change'] }>(
      `order/membership-plan-change/subscriptions/${encodeURIComponent(subscriptionId)}/changes/${encodeURIComponent(changeId)}/cancel`,
      {}
    );
  }
}

export interface PlanChangeNotice {
  title: string;
  body: string;
  /** "Switches to X on <date> · $Y/mo" for a scheduled change; null otherwise. */
  upcoming: string | null;
}

function usd(value: number | null | undefined): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    Number(value ?? 0)
  );
}

function onDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * The copy shown on the Active subscription screen after a change. Shared so
 * the single-membership and Choose Membership modals never drift apart.
 */
export function buildPlanChangeNotice(result: PlanChangeResult): PlanChangeNotice | null {
  const m = result.membership;
  const money = result.money;
  if (!m || !money) return null;
  const renews = onDate(m.renewal_date);
  const price = usd(money.price_from_renewal);

  if (result.change?.timing === 'next_cycle') {
    return {
      title: 'Change scheduled',
      body: `${m.old_plan.name} stays active until ${renews}.`,
      upcoming: `Switches to ${m.new_plan.name} on ${renews} · ${price}/mo`,
    };
  }

  const verb = result.change?.direction === 'downgrade' ? 'Downgraded' : 'Upgraded';
  let what: string;
  if (money.charged_today > 0) {
    what = `${usd(money.charged_today)} charged to the card on file.`;
  } else if (money.refunded_today > 0) {
    const capped = money.refund_capped_at !== null ? ' (capped at what was still refundable)' : '';
    what = `${usd(money.refunded_today)} refunded to the card on file${capped}.`;
  } else {
    what = 'No refund issued.';
  }
  return {
    title: `${verb} to ${m.new_plan.name}`,
    body: `${what} Renews at ${price}/mo on ${renews}.`,
    upcoming: null,
  };
}
